/**
 * Selah Union — App Registry
 *
 * Single source of truth for every app/tool/widget in the platform.
 * All registrations are inline to defeat tree-shaking.
 * Workspaces query this registry — no hardcoded imports in dashboards.
 *
 * Usage:
 *   import { getAppsForWorkspace, getAppById } from '@/apps/registry'
 *   const homeWidgets = getAppsForWorkspace('student', 'home')
 */

import type { UserRole } from '../components/navigation/tabConfigs'
import type { AppRegistration, AppRegistryMap, AppSurface } from './types'

// Widget components
import StreakWidget from './streak/StreakWidget'
import WeeklyProgressWidget from './weekly-progress/WeeklyProgressWidget'
import HeroWidget from './hero-card/HeroWidget'
import QuickTimerWidget from './quick-timer/QuickTimerWidget'
import ApureInsightWidget from './apure-insight/ApureInsightWidget'
import ContinuePracticeWidget from './continue-practice/ContinuePracticeWidget'
import NextLessonWidget from './next-lesson/NextLessonWidget'
import UpcomingScheduleWidget from './upcoming-schedule/UpcomingScheduleWidget'
import MessagesPreviewWidget from './messages-preview/MessagesPreviewWidget'
import PracticeTab from './student-practice/PracticeTab'
import MessagesTab from './student-messages/MessagesTab'
import YouTab from './student-you/YouTab'
import LessonsTab from './student-lessons/LessonsTab'

// Phase 4: Practice tool Widgets
import MetronomeWidget from './metronome/Widget'
import TunerWidget from './tuner/Widget'
import ScaleVisualizerWidget from './scale-visualizer/Widget'
import CircleOfFifthsWidget from './circle-of-fifths/Widget'
import BPMTapWidget from './bpm-tap/Widget'
import MidiPlayerWidget from './midi-player/Widget'
import SheetMusicWidget from './sheet-music/Widget'
import PracticeTimerWidget from './practice-timer/Widget'
import PdfViewerWidget from './pdf-viewer/Widget'

// Phase 4: Communication Widgets
import ApureWidget from './apure/Widget'

// Phase 4: Teacher Widgets
import TeacherContentStudioWidget from './teacher-content-studio/Widget'
import TeacherStudentsWidget from './teacher-students/Widget'
import TeacherScheduleEarningsWidget from './teacher-schedule-earnings/Widget'
import TeacherProfileWidget from './teacher-profile/Widget'

// Phase 4: Admin Widgets
import AdminFinanceWidget from './admin-finance/Widget'
import AdminOpsWidget from './admin-ops/Widget'

// Phase 4: Super Admin Widgets
import SuperAdminPlatformWidget from './super-admin-platform/Widget'
import SuperAdminSecurityWidget from './super-admin-security/Widget'

// ── Registry ────────────────────────────────────────────

const registry: AppRegistryMap = {}

// ── Query ───────────────────────────────────────────────

export function getAppById(id: string): AppRegistration | undefined {
  return registry[id]
}

export function getAppsForWorkspace(role: UserRole, workspace: string): AppRegistration[] {
  return Object.values(registry).filter(app => {
    if (!app.meta.roles.includes(role)) return false
    if (app.meta.workspace !== workspace) return false
    return true
  })
}

export function getAllAppsForRole(role: UserRole): AppRegistration[] {
  return Object.values(registry).filter(app => app.meta.roles.includes(role))
}

export function getAppSurface(
  appId: string,
  surface: AppSurface,
): AppRegistration['FullScreen'] | AppRegistration['Widget'] | AppRegistration['Compact'] | undefined {
  const app = registry[appId]
  if (!app) return undefined
  return app[surface]
}

export function listAppIds(): string[] {
  return Object.keys(registry)
}

// ── Registration function ───────────────────────────────

export function registerApp(registration: AppRegistration): void {
  if (registry[registration.meta.id]) {
    console.warn(`[Registry] App "${registration.meta.id}" already registered — overwriting.`)
  }
  registry[registration.meta.id] = registration
}

export function registerApps(registrations: AppRegistration[]): void {
  for (const reg of registrations) {
    registerApp(reg)
  }
}

// ── All registrations (inline to defeat tree-shaking) ───

const apps: AppRegistration[] = [
  {
    meta: { id: 'streak', name: 'Practice Streak', description: 'SVG donut showing current practice streak vs target', defaultSpan: 1, roles: ['student', 'guardian', 'parent'], moduleKey: 'streak', workspace: 'home' },
    Widget: StreakWidget,
    propsFromBff: (d: any) => ({ current: d?.streak?.current ?? 0, best: d?.streak?.best ?? 0, target: d?.weekly_goal?.target_min ?? 7 }),
  },
  {
    meta: { id: 'weekly-progress', name: 'Weekly Progress', description: 'Bar chart of daily practice minutes vs target', defaultSpan: 4, roles: ['student', 'guardian', 'parent'], moduleKey: 'weekly_progress', workspace: 'home' },
    Widget: WeeklyProgressWidget,
    propsFromBff: (d: any) => ({ goal: d?.weekly_goal }),
  },
  {
    meta: { id: 'hero-card', name: 'Hero Card', description: 'Full-width gradient hero with KPI pills and user greeting', defaultSpan: 4, roles: ['student', 'guardian', 'parent'], moduleKey: 'hero_card', workspace: 'home' },
    Widget: HeroWidget,
    propsFromBff: (d: any) => ({ user: d?.user, kpis: d?.kpis }),
  },
  {
    meta: { id: 'quick-timer', name: 'Quick Timer', description: 'Start a practice timer with preset durations', defaultSpan: 2, roles: ['student'], moduleKey: 'quick_timer', workspace: 'home' },
    Widget: QuickTimerWidget,
    propsFromBff: (d: any) => ({ presetsMin: d?.practice?.presets_min || [5, 10, 15, 30, 60] }),
  },
  {
    meta: { id: 'apure-insight', name: 'aPure Insight', description: 'AI-powered practice insight with action button', defaultSpan: 2, roles: ['student', 'guardian', 'parent'], moduleKey: 'apure_insight', workspace: 'home' },
    Widget: ApureInsightWidget,
    propsFromBff: (d: any) => ({ actionLabel: d?.insight?.action_label || 'Try aPure', text: d?.insight?.text || 'Your AI practice assistant' }),
  },
  {
    meta: { id: 'continue-practice', name: 'Continue Practice', description: 'Resume today\'s practice pick with one tap', defaultSpan: 2, roles: ['student'], moduleKey: 'continue_practice', workspace: 'home' },
    Widget: ContinuePracticeWidget,
    propsFromBff: (d: any) => ({ todayPick: d?.practice?.today_pick ?? null }),
  },
  {
    meta: { id: 'next-lesson', name: 'Next Lesson', description: 'Upcoming lesson card with teacher avatar and countdown', defaultSpan: 3, roles: ['student', 'guardian', 'parent'], moduleKey: 'next_lesson', workspace: 'home' },
    Widget: NextLessonWidget,
    propsFromBff: (d: any) => ({ lesson: d?.next_lesson }),
  },
  {
    meta: { id: 'upcoming-schedule', name: 'Upcoming Schedule', description: 'Horizontal schedule strip of next 5-7 days', defaultSpan: 4, roles: ['student', 'guardian', 'parent'], moduleKey: 'upcoming_schedule', workspace: 'home' },
    Widget: UpcomingScheduleWidget,
    propsFromBff: (d: any) => ({ nextLesson: d?.next_lesson, schedule: d?.schedule || [] }),
  },
  {
    meta: { id: 'messages-preview', name: 'Messages Preview', description: 'Compact Ligio preview — last message + open button', defaultSpan: 2, roles: ['student', 'guardian', 'parent'], moduleKey: 'messages_preview', workspace: 'home' },
    Widget: MessagesPreviewWidget,
    propsFromBff: (_d: any) => ({}),
  },
  // ── Student workspace tabs ──
  {
    meta: { id: 'student-practice', name: 'Practice Tools', description: '12-tool practice grid with timer bar', defaultSpan: 4, roles: ['student'], workspace: 'practice' },
    FullScreen: PracticeTab,
  },
  {
    meta: { id: 'student-messages', name: 'Messages', description: 'Conversation list with Ligio CTA', defaultSpan: 4, roles: ['student'], workspace: 'messages' },
    FullScreen: MessagesTab,
  },
  {
    meta: { id: 'student-you', name: 'You', description: 'Profile, settings, achievements, sign out', defaultSpan: 4, roles: ['student'], workspace: 'you' },
    FullScreen: YouTab,
  },
  {
    meta: { id: 'student-lessons', name: 'Lessons', description: 'Upcoming lessons, schedule, calendar, and teacher availability', defaultSpan: 4, roles: ['student'], workspace: 'lessons' },
    FullScreen: LessonsTab,
  },
  // ── Full-screen SPAs ──
  {
    meta: { id: 'ligio', name: 'Ligio Messenger', description: 'Full messaging, video calls, and real-time communication', defaultSpan: 4, roles: ['student', 'teacher', 'guardian', 'parent', 'admin', 'super_admin'], workspace: 'messages' },
    // FullScreen handled by React Router at /dashboard/ligio — no component to register
  },
  // ── Phase 4: Practice Tools ──
  {
    meta: { id: 'metronome', name: 'Metronome', description: 'Visual metronome with tap tempo, subdivisions, and sound', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: MetronomeWidget,
  },
  {
    meta: { id: 'tuner', name: 'Tuner', description: 'Chromatic instrument tuner with pitch detection', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: TunerWidget,
  },
  {
    meta: { id: 'scale-visualizer', name: 'Scale Visualizer', description: 'Interactive scale keyboard with note highlighting', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: ScaleVisualizerWidget,
  },
  {
    meta: { id: 'circle-of-fifths', name: 'Circle of Fifths', description: 'Interactive circle of fifths reference tool', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: CircleOfFifthsWidget,
  },
  {
    meta: { id: 'bpm-tap', name: 'BPM Tap', description: 'Tap tempo BPM counter for practice', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: BPMTapWidget,
  },
  {
    meta: { id: 'midi-player', name: 'MIDI Player', description: 'Playback MIDI files with visual piano roll', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: MidiPlayerWidget,
  },
  {
    meta: { id: 'sheet-music', name: 'Sheet Music', description: 'View and annotate sheet music scores', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: SheetMusicWidget,
  },
  {
    meta: { id: 'practice-timer', name: 'Practice Timer', description: 'Countdown timer with session tracking', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: PracticeTimerWidget,
  },
  {
    meta: { id: 'pdf-viewer', name: 'PDF Viewer', description: 'Upload and view sheet music PDFs', defaultSpan: 2, roles: ['student', 'teacher'], workspace: 'tools' },
    Widget: PdfViewerWidget,
  },
  // ── Phase 4: Communication ──
  {
    meta: { id: 'apure', name: 'aPure AI Tutor', description: 'AI-powered music theory tutor and practice assistant', defaultSpan: 2, roles: ['student', 'teacher', 'guardian', 'parent', 'admin', 'super_admin'], workspace: 'tools' },
    Widget: ApureWidget,
  },
  // ── Phase 4: Teacher Tools ──
  {
    meta: { id: 'teacher-content-studio', name: 'Content Studio', description: 'Create and manage lesson content and materials', defaultSpan: 3, roles: ['teacher'], workspace: 'teacher' },
    Widget: TeacherContentStudioWidget,
  },
  {
    meta: { id: 'teacher-students', name: 'My Students', description: 'Student roster, progress tracking, and assignments', defaultSpan: 3, roles: ['teacher'], workspace: 'teacher' },
    Widget: TeacherStudentsWidget,
  },
  {
    meta: { id: 'teacher-schedule-earnings', name: 'Schedule & Earnings', description: 'Lesson schedule and earnings dashboard', defaultSpan: 3, roles: ['teacher'], workspace: 'teacher' },
    Widget: TeacherScheduleEarningsWidget,
  },
  {
    meta: { id: 'teacher-profile', name: 'My Profile', description: 'Teacher profile, bio, rates, and settings', defaultSpan: 2, roles: ['teacher'], workspace: 'teacher' },
    Widget: TeacherProfileWidget,
  },
  // ── Phase 4: Admin Tools ──
  {
    meta: { id: 'admin-finance', name: 'Finance', description: 'Revenue reports, invoices, and payment tracking', defaultSpan: 3, roles: ['admin', 'super_admin'], workspace: 'admin' },
    Widget: AdminFinanceWidget,
  },
  {
    meta: { id: 'admin-ops', name: 'Operations', description: 'Teacher approvals, org settings, and compliance', defaultSpan: 3, roles: ['admin', 'super_admin'], workspace: 'admin' },
    Widget: AdminOpsWidget,
  },
  // ── Phase 4: Super Admin Tools ──
  {
    meta: { id: 'super-admin-platform', name: 'Platform', description: 'Platform-wide metrics, health, and system status', defaultSpan: 3, roles: ['super_admin'], workspace: 'super-admin' },
    Widget: SuperAdminPlatformWidget,
  },
  {
    meta: { id: 'super-admin-security', name: 'Security', description: 'Audit logs, access control, and security policies', defaultSpan: 3, roles: ['super_admin'], workspace: 'super-admin' },
    Widget: SuperAdminSecurityWidget,
  },
]

registerApps(apps)
